
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useToast } from '@/hooks/use-toast';

export interface Subscription {
  id: string;
  term: string;
  type: "keyword" | "category" | "cpv";
}

interface NotificationContextType {
  subscriptions: Subscription[];
  addSubscription: (subscription: Omit<Subscription, "id">) => void;
  removeSubscription: (id: string) => void;
  showNotification: (message: string, type?: 'success' | 'error' | 'info') => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const supabase = useSupabaseClient();
  const { toast } = useToast();

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile, error } = await supabase
          .from('profiles')
          .select('preferred_industries, preferred_countries')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        // Create a unified array of subscriptions
        const allSubscriptions: Subscription[] = [];
        
        // Add industry subscriptions
        if (profile?.preferred_industries) {
          profile.preferred_industries.forEach((industry: string) => {
            allSubscriptions.push({
              id: crypto.randomUUID(),
              term: industry,
              type: 'category'
            });
          });
        }
        
        // Add country subscriptions
        if (profile?.preferred_countries) {
          profile.preferred_countries.forEach((country: string) => {
            allSubscriptions.push({
              id: crypto.randomUUID(),
              term: `country:${country}`,
              type: 'keyword'
            });
          });
        }
        
        setSubscriptions(allSubscriptions);
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
    };

    loadPreferences();
  }, [supabase]);

  const addSubscription = async (subscription: Omit<Subscription, "id">) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const newSubscription = { ...subscription, id: crypto.randomUUID() };
      const updatedSubscriptions = [...subscriptions, newSubscription];
      
      // Separate industries and countries for database storage
      const industries = updatedSubscriptions
        .filter(sub => sub.type === 'category' || sub.type === 'cpv')
        .map(sub => sub.term);
        
      const countries = updatedSubscriptions
        .filter(sub => sub.type === 'keyword' && sub.term.startsWith('country:'))
        .map(sub => sub.term.replace('country:', ''));
      
      // Update both local state and profile
      const { error } = await supabase
        .from('profiles')
        .update({
          preferred_industries: industries,
          preferred_countries: countries
        })
        .eq('id', user.id);

      if (error) throw error;

      setSubscriptions(updatedSubscriptions);
      toast({
        title: "Success",
        description: "Subscription added successfully"
      });
    } catch (error) {
      console.error('Error adding subscription:', error);
      toast({
        title: "Error",
        description: "Failed to add subscription",
        variant: "destructive"
      });
    }
  };

  const removeSubscription = async (id: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const updatedSubscriptions = subscriptions.filter(sub => sub.id !== id);
      
      // Separate industries and countries for database storage
      const industries = updatedSubscriptions
        .filter(sub => sub.type === 'category' || sub.type === 'cpv')
        .map(sub => sub.term);
        
      const countries = updatedSubscriptions
        .filter(sub => sub.type === 'keyword' && sub.term.startsWith('country:'))
        .map(sub => sub.term.replace('country:', ''));
      
      // Update both local state and profile
      const { error } = await supabase
        .from('profiles')
        .update({
          preferred_industries: industries,
          preferred_countries: countries
        })
        .eq('id', user.id);

      if (error) throw error;

      setSubscriptions(updatedSubscriptions);
      toast({
        title: "Success",
        description: "Subscription removed successfully"
      });
    } catch (error) {
      console.error('Error removing subscription:', error);
      toast({
        title: "Error",
        description: "Failed to remove subscription",
        variant: "destructive"
      });
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    toast({
      title: type.charAt(0).toUpperCase() + type.slice(1),
      description: message,
      variant: type === 'error' ? 'destructive' : 'default',
    });
  };

  return (
    <NotificationContext.Provider value={{ subscriptions, addSubscription, removeSubscription, showNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
